import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Icon from './Icon';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../hooks/useAuth';
import { getNavItemsForRole, NavItem } from '../utils/navigationUtils';

// Design System Components
export const Card: React.FC<{
  children: React.ReactNode;
  className?: string;
  hover?: boolean;
  onClick?: () => void;
}> = ({ children, className = '', hover = false, onClick }) => (
  <div
    className={`rounded-2xl p-6 transition-all duration-300 ${
      hover ? 'hover:shadow-xl hover:-translate-y-1 cursor-pointer' : ''
    } ${className}`}
    style={{
      background: 'rgba(255,255,255,0.92)',
      border: '1px solid rgba(15,23,42,0.08)',
      boxShadow: '0 14px 30px rgba(15,23,42,0.08)'
    }}
    onClick={onClick}
  >
    {children}
  </div>
);

export const Button: React.FC<{
  children: React.ReactNode;
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  style?: React.CSSProperties;
  onClick?: () => void;
  disabled?: boolean;
  loading?: boolean;
  type?: 'button' | 'submit' | 'reset';
}> = ({
  children,
  variant = 'primary',
  size = 'md',
  className = '',
  style,
  onClick,
  disabled = false,
  loading = false,
  type = 'button'
}) => {
  const { getThemeClasses, getThemeStyles } = useTheme();
  const themeClasses = getThemeClasses();
  const themeStyles = getThemeStyles();

  const baseClasses = 'inline-flex items-center justify-center rounded-xl focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap tracking-[0.01em]';

  const variantClasses = {
    primary: '', // Stile gestito inline per il gradiente elegante
    secondary: 'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-900 dark:text-white focus:ring-gray-500',
    outline: 'border-2 border-gray-300 dark:border-gray-600 hover:border-blue-500 text-gray-700 dark:text-gray-300 hover:text-blue-600 focus:ring-blue-500',
    ghost: 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 focus:ring-gray-500',
    danger: '' // Stile gestito inline per il gradiente rosso
  };

  const sizeClasses = {
    sm: 'px-3.5 py-2 text-sm',
    md: 'px-4.5 py-2.5 text-base',
    lg: 'px-6 py-3 text-lg'
  };

  // Stile sobrio per i pulsanti primary (solid)
  const primaryButtonStyle = variant === 'primary' ? {
    background: themeStyles.primary,
    border: `1px solid ${themeStyles.primary}`,
    color: '#ffffff',
    fontSize: size === 'sm' ? '0.875rem' : size === 'lg' ? '1.0625rem' : '0.95rem',
    fontWeight: 700,
    padding: size === 'sm' ? '8px 14px' : size === 'lg' ? '14px 24px' : '10px 18px',
    borderRadius: '12px',
    cursor: 'pointer',
    boxShadow: '0 4px 14px rgba(15,23,42,0.08), 0 1px 2px rgba(203, 162, 77, 0.15)',
    transition: 'background 0.2s ease, box-shadow 0.2s ease, transform 0.1s ease',
    minHeight: '40px'
  } : {};

  // Stile per i pulsanti secondary (ghost/soft)
  const secondaryButtonStyle = variant === 'secondary' ? {
    background: '#f1f5f9',
    border: `1px solid ${themeStyles.border}`,
    color: '#0f172a',
    fontSize: size === 'sm' ? '0.875rem' : size === 'lg' ? '1.0625rem' : '0.95rem',
    fontWeight: 700,
    padding: size === 'sm' ? '8px 14px' : size === 'lg' ? '14px 24px' : '10px 18px',
    borderRadius: '12px',
    cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(15,23,42,0.04)',
    transition: 'background 0.2s ease, box-shadow 0.2s ease, transform 0.1s ease',
    minHeight: '40px'
  } : {};

  // Stile per i pulsanti danger (solid)
  const dangerButtonStyle = variant === 'danger' ? {
    background: '#dc2626',
    border: '1px solid #b91c1c',
    color: '#ffffff',
    fontSize: size === 'sm' ? '0.875rem' : size === 'lg' ? '1.0625rem' : '0.95rem',
    fontWeight: 700,
    padding: size === 'sm' ? '8px 14px' : size === 'lg' ? '14px 24px' : '10px 18px',
    borderRadius: '12px',
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(220, 38, 38, 0.15)',
    transition: 'background 0.2s ease, box-shadow 0.2s ease, transform 0.1s ease',
    minHeight: '40px'
  } : {};

  return (
    <button
      type={type}
      className={`design-system-button ${baseClasses} ${sizeClasses[size]} ${variantClasses[variant]} ${className}`}
      style={{ ...primaryButtonStyle, ...secondaryButtonStyle, ...dangerButtonStyle, ...style }}
      onClick={onClick}
      disabled={disabled || loading}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-1px)';
        if (variant === 'primary') {
          e.currentTarget.style.boxShadow = '0 6px 20px rgba(15,23,42,0.12), 0 2px 4px rgba(203, 162, 77, 0.2)';
        } else if (variant === 'secondary') {
          e.currentTarget.style.background = '#e2e8f0';
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(15,23,42,0.06)';
        } else if (variant === 'danger') {
          e.currentTarget.style.boxShadow = '0 6px 16px rgba(220,38,38,0.18)';
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        if (variant === 'primary') {
          e.currentTarget.style.boxShadow = '0 4px 14px rgba(15,23,42,0.08), 0 1px 2px rgba(203, 162, 77, 0.15)';
        } else if (variant === 'secondary') {
          e.currentTarget.style.background = '#f1f5f9';
          e.currentTarget.style.boxShadow = '0 2px 8px rgba(15,23,42,0.04)';
        } else if (variant === 'danger') {
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(220,38,38,0.15)';
        }
      }}
      onFocus={(e) => {
        // focus ring sobrio
        e.currentTarget.style.boxShadow = `${themeStyles.glow}`;
      }}
      onBlur={(e) => {
        // ripristina shadow base
        if (variant === 'primary') {
          e.currentTarget.style.boxShadow = '0 4px 14px rgba(15,23,42,0.08), 0 1px 2px rgba(203, 162, 77, 0.15)';
        } else if (variant === 'secondary') {
          e.currentTarget.style.boxShadow = '0 2px 8px rgba(15,23,42,0.04)';
        } else if (variant === 'danger') {
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(220,38,38,0.15)';
        }
      }}
    >
      {children}
    </button>
  );
};

export const Input: React.FC<{
  type?: string;
  placeholder?: string;
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  className?: string;
  error?: string;
  required?: boolean;
}> = ({ type = 'text', placeholder, value, onChange, className = '', error, required }) => {
  const { getThemeClasses } = useTheme();
  const themeClasses = getThemeClasses();

  return (
    <div className="space-y-1">
      <input
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        required={required}
        className={`design-system-input ${themeClasses.inputClasses} ${
          error ? 'border-red-300 focus:border-red-500 focus:ring-red-200' : ''
        } ${className}`}
      />
      {error && <p className="text-sm text-red-600 dark:text-red-400" style={{ marginTop: 6 }}>{error}</p>}
    </div>
  );
};

export const Badge: React.FC<{
  children: React.ReactNode;
  variant?: 'primary' | 'secondary' | 'success' | 'warning' | 'danger';
  className?: string;
}> = ({ children, variant = 'primary', className = '' }) => {
  const variantClasses = {
    primary: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    secondary: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200',
    success: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    warning: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
    danger: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
  };

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${variantClasses[variant]} ${className}`}>
      {children}
    </span>
  );
};

export const Section: React.FC<{
  title?: string;
  subtitle?: string;
  children: React.ReactNode;
  className?: string;
}> = ({ title, subtitle, children, className = '' }) => (
  <section className={`py-16 ${className}`}>
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      {(title || subtitle) && (
        <div className="text-center mb-12">
          {title && (
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              {title}
            </h2>
          )}
          {subtitle && (
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
              {subtitle}
            </p>
          )}
        </div>
      )}
      {children}
    </div>
  </section>
);

export const FeatureCard: React.FC<{
  icon: React.ReactNode;
  title: string;
  description: string;
  className?: string;
}> = ({ icon, title, description, className = '' }) => (
  <Card hover className={`text-center ${className}`}>
    <div className="mb-4 flex justify-center">{icon}</div>
    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
      {title}
    </h3>
    <p className="text-gray-600 dark:text-gray-400">
      {description}
    </p>
  </Card>
);

export const StatsCard: React.FC<{
  value: string;
  label: string;
  icon?: string;
  trend?: string;
  className?: string;
}> = ({ value, label, icon, trend, className = '' }) => (
  <Card className={`text-center ${className}`}>
    {icon && (
      <div className="text-3xl mb-2">
        <Icon name={icon} size={48} />
      </div>
    )}
    <div className="text-3xl font-bold text-gray-900 dark:text-white mb-1">
      {value}
    </div>
    <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">
      {label}
    </div>
    {trend && (
      <div className="text-sm text-green-600 dark:text-green-400">
        {trend}
      </div>
    )}
  </Card>
);

export const NavigationLink: React.FC<{
  to: string;
  children: React.ReactNode;
  className?: string;
  variant?: 'primary' | 'secondary';
}> = ({ to, children, className = '', variant = 'primary' }) => {
  const baseClasses = 'px-8 py-4 rounded-lg font-bold text-lg shadow-xl transition duration-300 inline-block';

  const variantClasses = {
    primary: 'text-blue-600 hover:text-black',
    secondary: 'text-gray-600 hover:text-white'
  };

  return (
    <Link
      to={to}
      className={`${baseClasses} ${variantClasses[variant]} ${className}`}
    >
      {children}
    </Link>
  );
};

export const NavigationBar: React.FC<{
  variant?: 'mobile' | 'desktop';
  className?: string;
}> = ({ variant = 'desktop', className = '' }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { getThemeStyles } = useTheme();
  const themeStyles = getThemeStyles();

  const navItems = getNavItemsForRole(user?.role);

  if (variant === 'mobile') {
    // Filtra e ordina gli elementi per ruolo per la navigazione mobile semplificata
    let mobileNavItems: NavItem[] = [];

    if (user?.role === 'tenant') {
      // Per inquilino: notifiche, candidature, cerca proprietari, cruscotto (da sinistra a destra)
      mobileNavItems = [
        navItems.find(item => item.path === '/notifiche'),
        navItems.find(item => item.path === '/candidature'),
        navItems.find(item => item.path === '/landlord-search'),
        navItems.find(item => item.path === '/cruscotto')
      ].filter(Boolean) as NavItem[];
    } else if (user?.role === 'landlord') {
      // Per proprietario: notifiche, candidature, ricerca inquilini, cruscotto (da sinistra a destra)
      mobileNavItems = [
        navItems.find(item => item.path === '/notifiche'),
        navItems.find(item => item.path === '/candidature'),
        navItems.find(item => item.path === '/tenant-search'),
        navItems.find(item => item.path === '/cruscotto')
      ].filter(Boolean) as NavItem[];
    } else {
      // Per altri ruoli, mantieni il comportamento precedente limitato
      mobileNavItems = navItems.slice(0, 4);
    }

    return (
      <nav className={`nav-mobile ${className}`} style={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(255,255,255,0.92)',
        borderTop: `1px solid ${themeStyles.border}`,
        display: 'flex',
        flexDirection: 'column',
        zIndex: 1100,
        boxShadow: '0 -10px 24px rgba(15,23,42,0.09)',
        borderTopLeftRadius: 20,
        borderTopRightRadius: 20,
        padding: '16px 8px'
      }}>
        {/* Riga inferiore: 4 elementi, ciascuno 25% larghezza */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: '4px',
          width: '100%'
        }}>
          {mobileNavItems.map((item, index) => {
            const isActive = window.location.pathname === item.path;
            return (
              <button
                key={`bottom-${index}`}
                className="nav-mobile-item touch-target"
                style={{
                  background: isActive ? themeStyles.primary : 'transparent',
                  border: isActive ? `1px solid ${themeStyles.primary}` : `1px solid transparent`,
                  borderRadius: '12px',
                  padding: '16px 8px', // Padding aumentato per icone più grandi
                  color: isActive ? '#ffffff' : themeStyles.textSecondary,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  flex: 1, // Ocupa 25% dello spazio
                  minHeight: '70px', // Altezza minima aumentata per icone più grandi
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: isActive ? '0 4px 12px rgba(15,23,42,0.08)' : 'none'
                }}
                onClick={() => navigate(item.path)}
                onTouchStart={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.background = themeStyles.hover;
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }
                }}
                onTouchEnd={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }
                }}
              >
                <span style={{
                  fontSize: 24, // Icona ancora più grande
                  marginBottom: 3,
                  filter: isActive ? 'none' : 'grayscale(0.3)'
                }}>
                  <Icon name={item.icon} size={24} />
                </span>
                <span style={{
                  fontSize: 12, // Testo ancora più grande
                  fontWeight: isActive ? '700' : '600',
                  opacity: isActive ? 1 : 0.8,
                  textAlign: 'center',
                  lineHeight: '1.1'
                }}>
                  {item.label}
                </span>
              </button>
            );
          })}
        </div>
      </nav>
    );
  }

  // Desktop variant
  return (
    <nav className={`navigation-bar-desktop ${className}`} style={{
      display: 'flex',
      alignItems: 'center',
      gap: '1rem'
    }}>
      {navItems.map((item, index) => {
        const isActive = window.location.pathname === item.path;
        return (
          <button
            key={index}
            style={{
              background: isActive ? themeStyles.primary : 'transparent',
              border: isActive ? `1px solid ${themeStyles.primary}` : `1px solid transparent`,
              borderRadius: '12px',
              padding: '12px 16px',
              color: isActive ? '#ffffff' : themeStyles.textSecondary,
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              fontSize: '14px',
              fontWeight: isActive ? '600' : '500',
              boxShadow: isActive ? '0 4px 12px rgba(15,23,42,0.08)' : 'none'
            }}
            onClick={() => navigate(item.path)}
            onMouseEnter={(e) => {
              if (!isActive) {
                e.currentTarget.style.background = themeStyles.hover;
                e.currentTarget.style.transform = 'translateY(-1px)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isActive) {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.transform = 'translateY(0)';
              }
            }}
          >
            <Icon name={item.icon} size={18} />
            <span>{item.label}</span>
          </button>
        );
      })}
    </nav>
  );
};

/**
 * StarRating Component - Rating interattivo con stelle
 */
export const StarRating: React.FC<{
  rating: number;
  maxRating?: number;
  size?: 'sm' | 'md' | 'lg';
  interactive?: boolean;
  onChange?: (rating: number) => void;
  className?: string;
}> = ({ rating, maxRating = 5, size = 'md', interactive = false, onChange, className = '' }) => {
  const [hoveredRating, setHoveredRating] = React.useState<number | null>(null);

  const sizeClasses = {
    sm: 'text-base',
    md: 'text-2xl',
    lg: 'text-4xl'
  };

  const displayRating = hoveredRating !== null ? hoveredRating : rating;

  const handleClick = (index: number) => {
    if (interactive && onChange) {
      onChange(index + 1);
    }
  };

  return (
    <div
      className={`flex gap-1 ${interactive ? 'cursor-pointer' : ''} ${sizeClasses[size]} ${className}`}
      onMouseLeave={() => interactive && setHoveredRating(null)}
      role={interactive ? 'radiogroup' : undefined}
      aria-label={`Rating: ${rating} di ${maxRating} stelle`}
    >
      {Array.from({ length: maxRating }, (_, index) => (
        <span
          key={index}
          onClick={() => handleClick(index)}
          onMouseEnter={() => interactive && setHoveredRating(index + 1)}
          className={`transition-all duration-200 ${
            index < displayRating 
              ? 'text-amber-400' 
              : 'text-gray-300 dark:text-gray-600'
          } ${
            interactive && index < displayRating 
              ? 'scale-110' 
              : ''
          }`}
          style={{ display: 'inline-block' }}
          role={interactive ? 'radio' : undefined}
          aria-checked={interactive ? index < rating : undefined}
        >
          ★
        </span>
      ))}
    </div>
  );
};

/**
 * Skeleton Component - Loading placeholder
 */
export const Skeleton: React.FC<{
  variant?: 'text' | 'rect' | 'circle';
  width?: string;
  height?: string;
  className?: string;
}> = ({ variant = 'text', width, height, className = '' }) => {
  const variantClasses = {
    text: 'h-4 rounded',
    rect: 'rounded-lg',
    circle: 'rounded-full'
  };

  return (
    <div
      className={`animate-pulse bg-gray-200 dark:bg-gray-700 ${variantClasses[variant]} ${className}`}
      style={{ width, height }}
      role="status"
      aria-label="Caricamento..."
    />
  );
};